import type { Resource } from '@medplum/fhirtypes';

import type { PatchOperation } from '../core/edit-store';

/**
 * The write seam. Structural on purpose: a MedplumClient satisfies it
 * directly, but @eluve/fhir-renderer never depends on @medplum/core (same
 * hygiene as the Apollo runtime-only coupling, ENG-5299).
 */
export type FhirWriteTransport = {
  patchResource(
    resourceType: Resource['resourceType'],
    id: string,
    operations: PatchOperation[],
  ): Promise<Resource>;
  /** POST a new resource. Commit uses this for drafts (no server id yet). */
  createResource(resource: Resource): Promise<Resource>;
};

export type FhirWriteError =
  /** Version test failed / 409 / 412 — resource changed since it was read. */
  | { kind: 'conflict'; message: string }
  /** Server AccessPolicy said no. Writes fail loudly, unlike reads. */
  | { kind: 'denied'; message: string }
  | { kind: 'invalid'; message: string }
  | { kind: 'network'; message: string };

export type CommitResult =
  | { ok: true; committed: number }
  | { ok: false; failures: Array<{ key: string; error: FhirWriteError }> };

export function classifyWriteError(error: unknown): FhirWriteError {
  const message = error instanceof Error ? error.message : String(error);
  const status =
    (error as { response?: { status?: number } })?.response?.status ??
    (error as { status?: number })?.status;
  // Medplum reports a failed JSONPatch `test` op (the optimistic lock) as a
  // 400 "Test failed: <expected> != <actual>", not a 412. Anchored and
  // status-gated so a 400 merely containing "test failed" stays invalid.
  if (
    status === 409 ||
    status === 412 ||
    /precondition/i.test(message) ||
    (status === 400 && /^test failed:/i.test(message))
  ) {
    return { kind: 'conflict', message };
  }
  if (status === 401 || status === 403) return { kind: 'denied', message };
  if (status === 400 || status === 422) return { kind: 'invalid', message };
  return { kind: 'network', message };
}
